import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createTermsAndConditions,
  getAllTermsAndConditions,
  getTermsAndConditionsByID,
  updateTermsAndConditions,
} from "./services/termsAndCondition.services";
import { clearTermsAndConditionDetails } from "./termsAndConditionSlice";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import IconButton from "../../components/common/IconButton";

const CreateTermsAndCondition: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const TermsAndConditionDetail = useAppSelector(
    (state) => state.termsandcondition.TermsAndConditionDetail
  );
  const TermsAndConditionID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector(
    (state) => state.termsandcondition.formLoading
  );

  useEffect(() => {
    if (TermsAndConditionID) {
      dispatch(getTermsAndConditionsByID(TermsAndConditionID));
    }
  }, [TermsAndConditionID]);

  useEffect(() => {
    return () => {
      dispatch(clearTermsAndConditionDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!TermsAndConditionID) {
        const insertPayload = {
          TermsAndConditions: values.TermsAndConditions,
          Description: values.Description,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(
          createTermsAndConditions(insertPayload)
        );
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearTermsAndConditionDetails());
          dispatch(getAllTermsAndConditions());
        }
      } else {
        const payload = {
          ID: TermsAndConditionID,
          TermsAndConditions: values.TermsAndConditions,
          Description: values.Description,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateTermsAndConditions(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearTermsAndConditionDetails());
          dispatch(getAllTermsAndConditions());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={TermsAndConditionDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "17px 0 8px",
              }}
            >
              <Typography.h4 className="m-0">
                {TermsAndConditionID
                  ? "Update Terms And Condition"
                  : "Add Terms And Condition"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
                icon={RxCross1}
              />
            </GridLayoutItem>

            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="TermsAndConditions"
                label="Terms And Conditions"
                placeholder="Terms And Conditions"
                component={FormTextField}
                astrike={true}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Description"
                label="Description"
                rows={8}
                astrike={true}
                component={FormRichTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"IsActive"}
                name={"IsActive"}
                label={"Status"}
                component={FormSelectionField}
                validator={requiredValidator}
                astrike={true}
                placeholder="i.e. Active"
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={TermsAndConditionID ? "Update" : "Save"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 10 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateTermsAndCondition;
